// HKDSE 2016 Paper 1 Knowledge Graph Samples - Complete Set
// Organized by question types with systematic naming

export const hkdse2016Samples_complete = {
  algebra: [
    {
      id: 'hkdse2016_algebra_01',
      problem: "Simplify $\\frac{(x^8 y^7)^2}{x^5 y^{-6}}$ and express the answer in positive index form.",
      problemType: "algebra_indices",
      difficulty: "intermediate",
      description: "Simplifying expressions with negative indices",
      graphData: {
        nodes: [
          {
            id: "negative_indices",
            name: "Negative Indices",
            type: "concepts",
            description: "Understanding negative indices and their properties",
            value: "a⁻ⁿ = 1/aⁿ",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "power_of_power",
            name: "Power of Power",
            type: "concepts",
            description: "When raising a power to another power, multiply the indices",
            value: "(aᵐ)ⁿ = aᵐⁿ",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "division_of_powers",
            name: "Division of Powers",
            type: "concepts",
            description: "When dividing powers with the same base, subtract the indices",
            value: "aᵐ ÷ aⁿ = aᵐ⁻ⁿ",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_simplify_numerator",
            name: "Step 1: Simplify Numerator",
            type: "steps",
            description: "Apply power of power rule to (x⁸y⁷)²",
            value: "(x⁸y⁷)² = x¹⁶y¹⁴",
            importance: 8,
            x: 0, y: 0, z: 25
          },
          {
            id: "step2_divide",
            name: "Step 2: Divide by Denominator",
            type: "steps",
            description: "Divide x¹⁶y¹⁴ by x⁵y⁻⁶",
            value: "x¹⁶y¹⁴ ÷ (x⁵y⁻⁶) = x¹¹y²⁰",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "The simplified expression in positive indices",
            value: "x¹¹y²⁰",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "power_of_power", to: "step1_simplify_numerator", type: "enables", strength: 9 },
          { from: "division_of_powers", to: "step2_divide", type: "enables", strength: 9 },
          { from: "step1_simplify_numerator", to: "step2_divide", type: "leads to", strength: 10 },
          { from: "step2_divide", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'hkdse2016_algebra_02',
      problem: "Given that $x$ is the subject of the formula $Ax = (4x + B)C$.",
      problemType: "algebra_formula",
      difficulty: "fundamental",
      description: "Rearranging formula to make a variable the subject",
      graphData: {
        nodes: [
          {
            id: "bracket_expansion",
            name: "Bracket Expansion",
            type: "concepts",
            description: "Expanding brackets using distributive law",
            value: "a(b + c) = ab + ac",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "collecting_terms",
            name: "Collecting Terms",
            type: "concepts",
            description: "Gathering like terms on one side",
            value: "Group terms with same variable",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "step1_expand_brackets",
            name: "Step 1: Expand Brackets",
            type: "steps",
            description: "Expand (4x + B)C",
            value: "Ax = 4xC + BC",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step2_collect_x_terms",
            name: "Step 2: Collect x Terms",
            type: "steps",
            description: "Move all x terms to one side",
            value: "Ax - 4xC = BC",
            importance: 8,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_factor_x",
            name: "Step 3: Factor x",
            type: "steps",
            description: "Factor out x from left side",
            value: "x(A - 4C) = BC",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "x expressed in terms of other variables",
            value: "x = BC/(A - 4C)",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "bracket_expansion", to: "step1_expand_brackets", type: "enables", strength: 10 },
          { from: "collecting_terms", to: "step2_collect_x_terms", type: "enables", strength: 9 },
          { from: "step1_expand_brackets", to: "step2_collect_x_terms", type: "leads to", strength: 9 },
          { from: "step2_collect_x_terms", to: "step3_factor_x", type: "leads to", strength: 9 },
          { from: "step3_factor_x", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'hkdse2016_algebra_03',
      problem: "Simplify $\\frac{2}{4x-5} + \\frac{3}{1-6x}$.",
      problemType: "algebra_fractions",
      difficulty: "intermediate",
      description: "Adding algebraic fractions with different denominators",
      graphData: {
        nodes: [
          {
            id: "common_denominator",
            name: "Common Denominator",
            type: "concepts",
            description: "Finding least common multiple of denominators",
            value: "LCM of (4x-5) and (1-6x)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "cross_multiplication",
            name: "Cross Multiplication",
            type: "concepts",
            description: "Method for adding fractions",
            value: "a/b + c/d = (ad + bc)/bd",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "step1_find_lcm",
            name: "Step 1: Find LCM",
            type: "steps",
            description: "LCM of (4x-5) and (1-6x)",
            value: "LCM = (4x-5)(1-6x)",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "step2_convert_fractions",
            name: "Step 2: Convert Fractions",
            type: "steps",
            description: "Convert both fractions to common denominator",
            value: "2(1-6x)/(4x-5)(1-6x) + 3(4x-5)/(4x-5)(1-6x)",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_expand_numerators",
            name: "Step 3: Expand Numerators",
            type: "steps",
            description: "Expand the numerators",
            value: "(2-12x + 12x-15)/(4x-5)(1-6x)",
            importance: 8,
            x: 0, y: 100, z: -15
          },
          {
            id: "step4_simplify",
            name: "Step 4: Simplify",
            type: "steps",
            description: "Combine like terms in numerator",
            value: "(-13)/(4x-5)(1-6x)",
            importance: 9,
            x: 0, y: 200, z: 0
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "Simplified fraction",
            value: "-13/(4x-5)(1-6x)",
            importance: 10,
            x: 0, y: 300, z: 0
          }
        ],
        links: [
          { from: "common_denominator", to: "step1_find_lcm", type: "enables", strength: 8 },
          { from: "cross_multiplication", to: "step2_convert_fractions", type: "enables", strength: 9 },
          { from: "step1_find_lcm", to: "step2_convert_fractions", type: "enables", strength: 9 },
          { from: "step2_convert_fractions", to: "step3_expand_numerators", type: "leads to", strength: 8 },
          { from: "step3_expand_numerators", to: "step4_simplify", type: "leads to", strength: 9 },
          { from: "step4_simplify", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  factorization: [
    {
      id: 'hkdse2016_factorization_01',
      problem: "Factorize: (a) $5m - 10n$ (b) $m^2 + mn - 6n^2$ (c) $m^2 + mn - 6n^2 - 5m + 10n$",
      problemType: "factorization",
      difficulty: "intermediate",
      description: "Factorization of expressions with multiple variables",
      graphData: {
        nodes: [
          {
            id: "common_factor",
            name: "Common Factor",
            type: "concepts",
            description: "Finding common factors in terms",
            value: "ab + ac = a(b + c)",
            importance: 9,
            x: -200, y: -200, z: 0
          },
          {
            id: "quadratic_factorization",
            name: "Quadratic Factorization",
            type: "concepts",
            description: "Factoring quadratic expressions",
            value: "Find factors of ac that add to b",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "part_a_common_factor",
            name: "Part (a): Common Factor",
            type: "steps",
            description: "Factor out common factor 5",
            value: "5m - 10n = 5(m - 2n)",
            importance: 8,
            x: 200, y: -200, z: 0
          },
          {
            id: "part_b_ac_method",
            name: "Part (b): AC Method",
            type: "steps",
            description: "Use AC method for m² + mn - 6n²",
            value: "AC = 1 × (-6) = -6, factors: 3 and -2",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "part_b_split_middle",
            name: "Part (b): Split Middle Term",
            type: "steps",
            description: "Rewrite as m² + 3mn - 2mn - 6n²",
            value: "m² + 3mn - 2mn - 6n²",
            importance: 8,
            x: 0, y: 100, z: -15
          },
          {
            id: "part_b_group_factor",
            name: "Part (b): Group and Factor",
            type: "steps",
            description: "Factor by grouping",
            value: "(m² + 3mn) + (-2mn - 6n²) = m(m + 3n) - 2n(m + 3n)",
            importance: 9,
            x: 0, y: 200, z: 0
          },
          {
            id: "part_b_final",
            name: "Part (b): Final Answer",
            type: "values",
            description: "Factored form",
            value: "(m + 3n)(m - 2n)",
            importance: 10,
            x: 0, y: 300, z: 0
          },
          {
            id: "part_c_rearrange",
            name: "Part (c): Rearrange",
            type: "steps",
            description: "Rearrange to use parts (a) and (b)",
            value: "(m² + mn - 6n²) - (5m - 10n)",
            importance: 8,
            x: -200, y: 150, z: 0
          },
          {
            id: "part_c_substitute",
            name: "Part (c): Substitute",
            type: "steps",
            description: "Use results from parts (a) and (b)",
            value: "(m + 3n)(m - 2n) - 5(m - 2n)",
            importance: 9,
            x: 200, y: 150, z: 0
          },
          {
            id: "part_c_final",
            name: "Part (c): Final Answer",
            type: "values",
            description: "Factored form for part (c)",
            value: "(m - 2n)(m + 3n - 5)",
            importance: 10,
            x: 0, y: 400, z: 0
          }
        ],
        links: [
          { from: "common_factor", to: "part_a_common_factor", type: "enables", strength: 8 },
          { from: "quadratic_factorization", to: "part_b_ac_method", type: "enables", strength: 9 },
          { from: "part_b_ac_method", to: "part_b_split_middle", type: "leads to", strength: 8 },
          { from: "part_b_split_middle", to: "part_b_group_factor", type: "leads to", strength: 9 },
          { from: "part_b_group_factor", to: "part_b_final", type: "equals", strength: 10 },
          { from: "part_a_common_factor", to: "part_c_rearrange", type: "enables", strength: 8 },
          { from: "part_b_final", to: "part_c_rearrange", type: "enables", strength: 8 },
          { from: "part_c_rearrange", to: "part_c_substitute", type: "leads to", strength: 9 },
          { from: "part_c_substitute", to: "part_c_final", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  word_problems: [
    {
      id: 'hkdse2016_word_problem_01',
      problem: "A recreation club has $180$ members and the number of male members is $40\\%$ more than the number of female members. Find the difference between the number of male members and the number of female members.",
      problemType: "word_problem_percentage",
      difficulty: "intermediate",
      description: "Solving percentage word problems with variables",
      graphData: {
        nodes: [
          {
            id: "total_members",
            name: "Total Members",
            type: "concepts",
            description: "Total number of members",
            value: "Total = 180",
            importance: 8,
            x: -200, y: -200, z: 0
          },
          {
            id: "percentage_relationship",
            name: "Percentage Relationship",
            type: "concepts",
            description: "Male members 40% more than female",
            value: "Males = Females + 40% of Females",
            importance: 9,
            x: 0, y: -200, z: 0
          },
          {
            id: "step1_setup_variables",
            name: "Step 1: Setup Variables",
            type: "steps",
            description: "Let number of females be x",
            value: "Females = x, Males = 1.4x",
            importance: 9,
            x: 200, y: -200, z: 0
          },
          {
            id: "step2_setup_equation",
            name: "Step 2: Setup Equation",
            type: "steps",
            description: "Total members equation",
            value: "x + 1.4x = 180",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_solve",
            name: "Step 3: Solve for x",
            type: "steps",
            description: "Solve the linear equation",
            value: "2.4x = 180 → x = 75",
            importance: 8,
            x: 0, y: 100, z: -15
          },
          {
            id: "step4_find_males",
            name: "Step 4: Find Males",
            type: "steps",
            description: "Calculate number of males",
            value: "Males = 1.4 × 75 = 105",
            importance: 8,
            x: 0, y: 200, z: 0
          },
          {
            id: "step5_find_difference",
            name: "Step 5: Find Difference",
            type: "steps",
            description: "Calculate the difference",
            value: "Difference = 105 - 75 = 30",
            importance: 10,
            x: 0, y: 300, z: 0
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "Difference between male and female members",
            value: "30 members",
            importance: 10,
            x: 0, y: 400, z: 0
          }
        ],
        links: [
          { from: "total_members", to: "step2_setup_equation", type: "enables", strength: 8 },
          { from: "percentage_relationship", to: "step1_setup_variables", type: "enables", strength: 9 },
          { from: "step1_setup_variables", to: "step2_setup_equation", type: "enables", strength: 9 },
          { from: "step2_setup_equation", to: "step3_solve", type: "leads to", strength: 8 },
          { from: "step3_solve", to: "step4_find_males", type: "enables", strength: 8 },
          { from: "step4_find_males", to: "step5_find_difference", type: "enables", strength: 10 },
          { from: "step5_find_difference", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  inequalities: [
    {
      id: 'hkdse2016_inequalities_01',
      problem: "Consider the compound inequality $x + 6 < 6(x + 11)$ or $x \\leq -5$ ...................... (*) (a) Solve (*). (b) Write down the greatest negative integer that satisfies (*).",
      problemType: "inequalities_compound",
      difficulty: "intermediate",
      description: "Solving compound inequalities with 'or' condition",
      graphData: {
        nodes: [
          {
            id: "compound_inequality",
            name: "Compound Inequality",
            type: "concepts",
            description: "Inequality with 'or' condition",
            value: "Inequality 1 OR Inequality 2",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "inequality1",
            name: "Inequality 1",
            type: "concepts",
            description: "First inequality to solve",
            value: "x + 6 < 6(x + 11)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "inequality2",
            name: "Inequality 2",
            type: "concepts",
            description: "Second inequality",
            value: "x ≤ -5",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_solve_ineq1",
            name: "Step 1: Solve Inequality 1",
            type: "steps",
            description: "Solve x + 6 < 6(x + 11)",
            value: "x + 6 < 6x + 66",
            importance: 9,
            x: -200, y: 0, z: 25
          },
          {
            id: "step1a_expand",
            name: "Step 1a: Expand",
            type: "steps",
            description: "Expand right side",
            value: "x + 6 < 6x + 66",
            importance: 8,
            x: -200, y: 50, z: 0
          },
          {
            id: "step1b_collect_terms",
            name: "Step 1b: Collect Terms",
            type: "steps",
            description: "Collect like terms",
            value: "-60 < 5x",
            importance: 8,
            x: -200, y: 100, z: 0
          },
          {
            id: "step1c_solve",
            name: "Step 1c: Solve for x",
            type: "steps",
            description: "Solve the inequality",
            value: "x > -12",
            importance: 9,
            x: -200, y: 150, z: 0
          },
          {
            id: "step2_combine_solutions",
            name: "Step 2: Combine Solutions",
            type: "steps",
            description: "Combine with 'or' condition",
            value: "x > -12 OR x ≤ -5",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "step3_simplify",
            name: "Step 3: Simplify",
            type: "steps",
            description: "Simplify the solution set",
            value: "All real numbers (since x > -12 covers all cases)",
            importance: 8,
            x: 0, y: 200, z: 0
          },
          {
            id: "part_b_greatest_negative",
            name: "Part (b): Greatest Negative Integer",
            type: "steps",
            description: "Find greatest negative integer satisfying (*)",
            value: "Since all real numbers satisfy (*), there is no greatest negative integer",
            importance: 8,
            x: 0, y: 300, z: 0
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "Complete solution",
            value: "(a) All real numbers (b) No such integer exists",
            importance: 10,
            x: 0, y: 400, z: 0
          }
        ],
        links: [
          { from: "compound_inequality", to: "inequality1", type: "includes", strength: 9 },
          { from: "compound_inequality", to: "inequality2", type: "includes", strength: 9 },
          { from: "inequality1", to: "step1_solve_ineq1", type: "requires", strength: 9 },
          { from: "step1_solve_ineq1", to: "step1a_expand", type: "leads to", strength: 8 },
          { from: "step1a_expand", to: "step1b_collect_terms", type: "leads to", strength: 8 },
          { from: "step1b_collect_terms", to: "step1c_solve", type: "leads to", strength: 9 },
          { from: "step1c_solve", to: "step2_combine_solutions", type: "enables", strength: 9 },
          { from: "inequality2", to: "step2_combine_solutions", type: "enables", strength: 9 },
          { from: "step2_combine_solutions", to: "step3_simplify", type: "leads to", strength: 8 },
          { from: "step3_simplify", to: "part_b_greatest_negative", type: "enables", strength: 8 },
          { from: "step3_simplify", to: "final_answer", type: "contributes to", strength: 9 },
          { from: "part_b_greatest_negative", to: "final_answer", type: "contributes to", strength: 8 }
        ]
      }
    }
  ],
  
  trigonometry: [
    {
      id: 'hkdse2016_trigonometry_01',
      problem: "In a polar coordinate system, $O$ is the pole. The polar coordinates of points $A$ and $B$ are $(12, 75°)$ and $(12, 135°)$ respectively. (a) Find $\\angle AOB$. (b) Find the perimeter of $\\triangle AOB$. (c) Write down the number of lines of symmetry of $\\triangle AOB$.",
      problemType: "trigonometry_polar_coordinates",
      difficulty: "intermediate",
      description: "Solving trigonometry problems using polar coordinates",
      graphData: {
        nodes: [
          {
            id: "polar_coordinates",
            name: "Polar Coordinates",
            type: "concepts",
            description: "Understanding polar coordinate system",
            value: "A(12, 75°), B(12, 135°)",
            importance: 9,
            x: -200, y: -200, z: 0
          },
          {
            id: "angle_aob",
            name: "Angle AOB",
            type: "concepts",
            description: "Angle between OA and OB",
            value: "∠AOB = 135° - 75° = 60°",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "isosceles_triangle",
            name: "Isosceles Triangle",
            type: "concepts",
            description: "Triangle with two equal sides",
            value: "OA = OB = 12",
            importance: 9,
            x: 200, y: -200, z: 0
          },
          {
            id: "part_a_angle_calculation",
            name: "Part (a): Angle Calculation",
            type: "steps",
            description: "Calculate angle AOB",
            value: "∠AOB = 135° - 75° = 60°",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "part_b_cosine_law",
            name: "Part (b): Cosine Law",
            type: "steps",
            description: "Use cosine law to find AB",
            value: "AB² = OA² + OB² - 2×OA×OB×cos(60°)",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "part_b_calculation",
            name: "Part (b): Calculation",
            type: "steps",
            description: "Calculate AB and perimeter",
            value: "AB² = 144 + 144 - 288×0.5 = 144 → AB = 12",
            importance: 8,
            x: 0, y: 200, z: 0
          },
          {
            id: "part_b_perimeter",
            name: "Part (b): Perimeter",
            type: "steps",
            description: "Calculate perimeter",
            value: "Perimeter = 12 + 12 + 12 = 36",
            importance: 8,
            x: 0, y: 300, z: 0
          },
          {
            id: "part_c_symmetry",
            name: "Part (c): Lines of Symmetry",
            type: "steps",
            description: "Find lines of symmetry",
            value: "Equilateral triangle has 3 lines of symmetry",
            importance: 8,
            x: 0, y: 400, z: 0
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "Complete solution",
            value: "(a) 60° (b) 36 (c) 3",
            importance: 10,
            x: 0, y: 500, z: 0
          }
        ],
        links: [
          { from: "polar_coordinates", to: "angle_aob", type: "enables", strength: 10 },
          { from: "angle_aob", to: "part_a_angle_calculation", type: "equals", strength: 10 },
          { from: "isosceles_triangle", to: "part_b_cosine_law", type: "enables", strength: 9 },
          { from: "part_a_angle_calculation", to: "part_b_cosine_law", type: "enables", strength: 9 },
          { from: "part_b_cosine_law", to: "part_b_calculation", type: "leads to", strength: 8 },
          { from: "part_b_calculation", to: "part_b_perimeter", type: "leads to", strength: 8 },
          { from: "part_b_perimeter", to: "part_c_symmetry", type: "enables", strength: 8 },
          { from: "part_a_angle_calculation", to: "final_answer", type: "contributes to", strength: 10 },
          { from: "part_b_perimeter", to: "final_answer", type: "contributes to", strength: 8 },
          { from: "part_c_symmetry", to: "final_answer", type: "contributes to", strength: 8 }
        ]
      }
    }
  ],
  
  functions: [
    {
      id: 'hkdse2016_functions_01',
      problem: "It is known that $f(x)$ is the sum of two parts, one part varies directly as $x$, and the other part varies directly as $x^2$. Suppose $f(3) = 48$ and $f(9) = 198$. (a) Find $f(x)$. (b) Solve the equation $f(x) = 90$.",
      problemType: "functions_variation",
      difficulty: "intermediate",
      description: "Solving problems with direct variation functions",
      graphData: {
        nodes: [
          {
            id: "direct_variation",
            name: "Direct Variation",
            type: "concepts",
            description: "Understanding direct variation",
            value: "y varies directly as x: y = kx",
            importance: 9,
            x: -200, y: -200, z: 0
          },
          {
            id: "function_form",
            name: "Function Form",
            type: "concepts",
            description: "f(x) = ax + bx²",
            value: "f(x) = ax + bx²",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "given_conditions",
            name: "Given Conditions",
            type: "concepts",
            description: "f(3) = 48, f(9) = 198",
            value: "Two equations with two unknowns",
            importance: 9,
            x: 200, y: -200, z: 0
          },
          {
            id: "step1_setup_equations",
            name: "Step 1: Setup Equations",
            type: "steps",
            description: "Set up system of equations",
            value: "3a + 9b = 48, 9a + 81b = 198",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step2_solve_system",
            name: "Step 2: Solve System",
            type: "steps",
            description: "Solve the system of equations",
            value: "a = 6, b = 2",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "part_a_final_function",
            name: "Part (a): Final Function",
            type: "steps",
            description: "Write the function",
            value: "f(x) = 6x + 2x²",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "part_b_setup_equation",
            name: "Part (b): Setup Equation",
            type: "steps",
            description: "Set f(x) = 90",
            value: "6x + 2x² = 90",
            importance: 8,
            x: 0, y: 300, z: 0
          },
          {
            id: "part_b_solve_quadratic",
            name: "Part (b): Solve Quadratic",
            type: "steps",
            description: "Solve the quadratic equation",
            value: "2x² + 6x - 90 = 0 → x² + 3x - 45 = 0",
            importance: 9,
            x: 0, y: 400, z: 0
          },
          {
            id: "part_b_final_answer",
            name: "Part (b): Final Answer",
            type: "steps",
            description: "Find x values",
            value: "x = (-3 ± √189)/2",
            importance: 8,
            x: 0, y: 500, z: 0
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "Complete solution",
            value: "(a) f(x) = 6x + 2x² (b) x = (-3 ± √189)/2",
            importance: 10,
            x: 0, y: 600, z: 0
          }
        ],
        links: [
          { from: "direct_variation", to: "function_form", type: "enables", strength: 9 },
          { from: "function_form", to: "step1_setup_equations", type: "enables", strength: 9 },
          { from: "given_conditions", to: "step1_setup_equations", type: "enables", strength: 9 },
          { from: "step1_setup_equations", to: "step2_solve_system", type: "leads to", strength: 9 },
          { from: "step2_solve_system", to: "part_a_final_function", type: "leads to", strength: 10 },
          { from: "part_a_final_function", to: "part_b_setup_equation", type: "enables", strength: 8 },
          { from: "part_b_setup_equation", to: "part_b_solve_quadratic", type: "leads to", strength: 9 },
          { from: "part_b_solve_quadratic", to: "part_b_final_answer", type: "leads to", strength: 8 },
          { from: "part_a_final_function", to: "final_answer", type: "contributes to", strength: 10 },
          { from: "part_b_final_answer", to: "final_answer", type: "contributes to", strength: 8 }
        ]
      }
    }
  ]
}; 